import { AspectRatio } from '@/components/ui/atoms/aspect-ratio'
import { Button } from '@/components/ui/atoms/button'
import EmptyList from '@/components/ui/atoms/empty-list'
import { toast } from '@/components/ui/molecules/use-toast'
import type { IArticle } from '@/types/article'
import type { ICursor } from '@/types/cursor'
import { ChevronDown, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

export default function ArticlesList({
  tags: _ssr_tags,
  nodes: ssr_articles,
  cursor: ssr_cursor,
  limit,
  search,
}: {
  tags?: Array<string>
  nodes: Array<IArticle>
  cursor: ICursor
  limit: number
  search?: string
}) {
  const [ssrArticles, _setSsrArticles] = useState(ssr_articles)
  const [ssrCursor, setSsrCursor] = useState(ssr_cursor)

  const articlesRef = useRef<HTMLElement | null>(null)

  const [articles, setArticles] = useState<Array<IArticle>>(
    ssrArticles.slice(0, limit),
  )
  const [cursor, setCursor] = useState<ICursor>({
    ...ssrCursor,
  })

  const [loading, setLoading] = useState<boolean>(false)

  const getMore = async () => {
    const request = await fetch('/api/proxy/articles', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ cursor: ssrCursor.endCursor, search }, null, 2),
    })

    if (request.status !== 200)
      toast({
        variant: 'destructive',
        title: 'Algo não saiu como esperado.',
        description: 'Houve um problema com a sua requisição.',
      })
    else {
      const json = await request.json()
      setSsrCursor(json.cursor)
      setArticles((prev) => [...prev, ...json.nodes])
      setCursor(json.cursor)
    }
  }

  const handleScroll = useCallback(() => {
    if (articlesRef.current) {
      articlesRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      const offset = 100
      const elementPosition =
        articlesRef.current?.getBoundingClientRect().top + window.scrollY
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }, [])

  const handleClick = useCallback(async () => {
    setLoading(true)

    await getMore()

    setLoading(false)
  }, [setLoading, getMore])

  useEffect(() => {
    if (search?.length) {
      handleScroll()
    }
  }, [])

  return (
    <>
      <section className="mt-8" ref={articlesRef}>
        <h2 className="text-2xl font-bold tracking-tight text-zinc-700">
          {search ? `Artigos relacionados a: "${search}"` : 'Artigos recentes'}
        </h2>
      </section>

      {articles.length === 0 && <EmptyList pathname="/artigos" />}
      <div className="mx-auto mb-10 grid grid-cols-1 gap-x-8 gap-y-16 sm:pt-7 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3">
        {articles.map((item) => (
          <article
            key={item.slug}
            className="flex max-w-xl flex-col items-start justify-between self-baseline"
          >
            <div
              className={
                'relative rounded-xl my-3 sm:mb-0 w-full h-full clip-path-inset'
              }
            >
              <a href={item.href}>
                <AspectRatio ratio={16 / 9}>
                  <img
                    loading="lazy"
                    src={item.media || '/assets/images/no-article-image.png'}
                    width="0"
                    height="0"
                    sizes="100%"
                    className="rounded-xl w-full h-full object-cover bg-gradient-to-tr from-zinc-300"
                    alt="Banner do artigos dos professores"
                  />
                </AspectRatio>
              </a>
            </div>
            <div className="group relative">
              {item.tags && item.tags?.length > 0 && (
                <p className="font-medium tracking-wider text-xs mt-3 uppercase text-sky-600">
                  {item?.tags[0].name}
                </p>
              )}
              <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600">
                <a href={item.href}>
                  <span className="absolute inset-0" />
                  {item.title}
                </a>
              </h3>
              <div
                className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600"
                dangerouslySetInnerHTML={{
                  __html: item.text?.html || '',
                }}
              />
            </div>
            <div className="relative mt-4 flex items-center justify-between gap-x-4">
              <img
                loading="lazy"
                src={item?.author?.avatarUrl || ''}
                alt={item?.author?.name || `Autor - ${item.title}`}
                className="h-10 w-10 rounded-full bg-gradient-to-tr from-zinc-300"
                width={40}
                height={40}
              />
              <div className="text-sm leading-6">
                <a
                  href={`/autor/${item?.author?.slug}`}
                  className="font-semibold text-gray-900 hover:text-sky-600 hover:underline block"
                >
                  {item?.author?.name}
                </a>
                <time dateTime={item.date.d} className="text-gray-500">
                  {item.date.dl}
                </time>
              </div>
            </div>
          </article>
        ))}
      </div>

      {(cursor.hasNextPage || ssrCursor.hasNextPage) && (
        <Button
          disabled={!cursor.hasNextPage || loading}
          variant={'outline'}
          onClick={handleClick}
          className="w-full text-primary border border-orange-500 hover:bg-orange-500 hover:text-white"
        >
          {!loading ? (
            <ChevronDown className="mr-2 h-4 w-4 animate-bounce" />
          ) : (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Ver mais artigos
        </Button>
      )}
    </>
  )
}
